import { el, type ElementProps, jsx } from '@amazon/vinyl-tsx'
import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import type { SearchOptions, SearchRecommender } from './SearchRecommender'
import { Abort, createDisposer, globalRef, withAbort } from '@amazon/vinyl-util'
import { back, navigateTo } from '@/router/router'
import { windowEvents } from '@/util/interaction'
import { createSearchRecommender } from './searchService'
import { RecommendationsShoveler } from '@/browse/RecommendationsShoveler'
import { ctx } from '@/util/context'
import type { RecommendationRendererDeps } from '@/browse/RecommendationRenderer'
import { getString } from '@/search/i18n/bundle.generated'
import { clearSearchHistory, getSearchHistory, recordSearchQuery } from '@/search/searchHistory'
import { fetchFindRecommendations } from '@/catalog/catalogService'
import { ImagePlaceholder } from '@/components/ImagePlaceholder'
import { ImageView, type ImageModel } from '@/components/ImageView'
import { createImageModel } from '@/util/ImageUrl'
import { Icon } from '@/components/Icon'
import { times } from '@/util/array'

// Maps the server-returned widget label (set in `defaultSearchSpecifications`)
// to the i18n key that hornet uses for that section header. Anything not in
// the map renders the raw server string as a fallback. We keep the call to
// `getString(literal)` so each entry sees a literal-typed key (the bundle's
// overload requires no params for these; passing a union widens it to
// "expects params" and the call no longer type-checks).
const SECTION_LABEL_RESOLVERS: Readonly<Partial<Record<string, () => ObservableValue<string>>>> = {
    TopResults: () => getString('dmusic_find_top_results_label'),
    Albums: () => getString('dmusic_find_album_results_label'),
    Artists: () => getString('dmusic_find_artist_results_label'),
    Playlists: () => getString('dmusic_find_playlist_results_label'),
    Songs: () => getString('dmusic_find_song_results_label'),
    Stations: () => getString('dmusic_find_station_results_label'),
    Podcasts: () => getString('dmusic_find_podcast_results_label'),
    PodcastEpisodes: () => getString('dmusic_find_podcast_episode_results_label'),
    Videos: () => getString('dmusic_find_video_results_label'),
}

function localizeSectionLabel(serverLabel: string): ObservableValue<string> | string {
    const fn = SECTION_LABEL_RESOLVERS[serverLabel]
    return fn ? fn() : serverLabel
}

const { errorHandler } = ctx('search')

export type SearchPropsDeps = RecommendationRendererDeps

export type SearchProps = ElementProps<'div'> & {
    readonly controller: MutableValue<SearchOptions | null>
    readonly deps: SearchPropsDeps
}

const searchRecommender: SearchRecommender = createSearchRecommender()

export function Search(props: SearchProps) {
    const controller = props.controller
    const imageSize = 56
    const noResultsFound = <p>{getString('dmusic_find_empty_search_results')}</p>
    return (
        <div
            {...props}
            className={`search ${props.className ?? ''}`}
            onConnect={(view) => {
                const { add, dispose } = createDisposer()
                // Flip the route-mode observable on while this page is
                // mounted. The header listens to it to switch the
                // header into search-mode (input expanded, etc.).
                getSearchRouteRef().value = true
                add(() => {
                    getSearchRouteRef().value = false
                })
                // Sync the controller with the URL on mount and on any
                // back/forward navigation — the query is stored in the
                // `?q=` search param so links and history entries
                // round-trip.
                const syncFromUrl = () => {
                    const query = new URLSearchParams(window.location.search).get('q') ?? ''
                    if (controller.value?.query !== query) {
                        controller.value = { query }
                    }
                }
                syncFromUrl()
                add(windowEvents.on('popstate', syncFromUrl))
                // Auto-focus the header search input when the page
                // mounts. The header subscribes to searchRouteRef and
                // will also focus on the flip, but a same-microtask
                // observer flip can lose to a freshly mounted input
                // that subscribes after the value already settled —
                // calling focus() here is the belt to that
                // suspenders. queueMicrotask defers past the page's
                // own scroll-restoration so focus doesn't get
                // immediately stolen back.
                queueMicrotask(() => {
                    const input = document.querySelector<HTMLInputElement>('#header .searchInput')
                    input?.focus()
                })
                let abortQuery = new Abort()
                add(
                    controller.onData((current) => {
                        abortQuery.abort()
                        abortQuery = new Abort()
                        const searchResultsDiv = el('.searchResults', view)
                        searchResultsDiv.innerHTML = ''
                        if (current?.query) {
                            withAbort(searchRecommender(current), abortQuery)
                                .then((results) => {
                                    if (!results.length) {
                                        searchResultsDiv.append(noResultsFound)
                                    } else {
                                        // Record the query in the local
                                        // search-history list so the idle
                                        // pane can offer it back to the
                                        // user as a one-tap repeat.
                                        recordSearchQuery(current.query)
                                        results.forEach((result) => {
                                            searchResultsDiv.append(
                                                <div className="recommendationContainer">
                                                    <RecommendationsShoveler
                                                        className={`small recommendations`}
                                                        headerTitle={localizeSectionLabel(
                                                            result.label
                                                        )}
                                                        header={
                                                            <h2>
                                                                {localizeSectionLabel(result.label)}
                                                            </h2>
                                                        }
                                                        imageSize={imageSize}
                                                        data={result.items}
                                                        deps={props.deps}
                                                    />
                                                </div>
                                            )
                                        })
                                    }
                                })
                                .catch(errorHandler)
                        }
                    })
                )

                return dispose
            }}
        >
            <div className="header" visible={controller.map((v) => !!v?.query)}>
                <h1>
                    {getString('dmusic_find_search_results_text', {
                        searchTerm: controller.map((v) => v?.query ?? ''),
                    })}
                </h1>
            </div>
            <div
                className={`searchResults musicCollections ${props.className ?? ''}`}
                visible={controller.map((v) => !!v?.query)}
            />
            {/*
              Idle state: when the input is empty (just opened the
              search page or the user cleared the query), surface
              recent searches as one-tap re-runs. Falls back to the
              old placeholder text when the user hasn't searched yet.
              Mirrors green hornet's SearchHistory pane.
            */}
            <SearchIdle
                visible={controller.map((v) => !v?.query)}
                onSelect={(q) => {
                    // Route through updateSearchQuery so the URL mirrors
                    // the selected history pill (link-shareable) and
                    // popstate can restore it.
                    updateSearchQuery(q)
                    const input = document.querySelector<HTMLInputElement>('#header .searchInput')
                    if (input) input.value = q
                }}
            />
        </div>
    )
}

interface SearchIdleProps {
    readonly visible: ObservableValue<boolean>
    readonly onSelect: (query: string) => void
}

interface CategoryTile {
    readonly id: string
    readonly name: string
    /** Router-ready pathname (never an absolute URL). */
    readonly href: string | null
    readonly image: ImageModel
}

/**
 * Turn Firefly's Category url/id into a Bullfrog router path.
 *
 * - Audiobook categories route to `/audiobooks/categories/:id` — GH does
 *   the same because the audiobook browse routes aren't reachable via
 *   the Category's own `url`.
 * - Any other Category with a `url` gets normalized to its pathname —
 *   Firefly hands back absolute URLs (music.amazon.com/…), the router
 *   only understands paths.
 */
function categoryHref(id: string, url: string | null | undefined): string | null {
    if (id.includes('AUDIOBOOKS')) return `/audiobooks/categories/${id}`
    if (!url) return null
    try {
        return new URL(url, window.location.origin).pathname
    } catch {
        return null
    }
}

interface CategoryGroupingSection {
    readonly id: string
    readonly name: string
    readonly tiles: readonly CategoryTile[]
}

function SearchIdle(props: SearchIdleProps) {
    const history = getSearchHistory()
    const hasHistory = history.map((items) => items.length > 0)
    const groupings = data<readonly CategoryGroupingSection[] | null>(null)

    // Lazy-load the category groupings the first time the idle pane
    // mounts — saves the round-trip when search lands and the user
    // immediately types. Cached at the module level via gqlFetch's
    // cache layer, so a second mount returns instantly.
    let fetchStarted = false
    function fetchGroupingsOnce() {
        if (fetchStarted) return
        fetchStarted = true
        fetchFindRecommendations()
            .then((res) => {
                const recs = res.browseRecommendations ?? []
                const sections: CategoryGroupingSection[] = []
                for (const rec of recs) {
                    if (!rec.id || !rec.name) continue
                    const edges = rec.recommendedContent?.edges ?? []
                    const tiles: CategoryTile[] = []
                    for (const edge of edges) {
                        const node = edge.node
                        if (node.__typename !== 'Category') continue
                        if (!node.id || !node.name) continue
                        // GH's createUnpinnedCategoryGroupingItem picks
                        // node.images[1] as the tile artwork and falls
                        // back to images[0]. Firefly returns them in a
                        // stable order (thumb-square, wide-banner),
                        // not by imageType — so index-based selection
                        // matches GH exactly.
                        const image = node.images?.[1] ?? node.images?.[0] ?? null
                        tiles.push({
                            id: node.id,
                            name: node.name,
                            href: categoryHref(node.id, node.url),
                            // Firefly's Category images are pre-baked
                            // 288×72 banners (FindPageImagePill) meant
                            // to fill the entire tile. Ask the CDN for
                            // the native 288-wide asset.
                            image: createImageModel(image, { scaleToWidth: 288 }),
                        })
                    }
                    if (tiles.length === 0) continue
                    sections.push({ id: rec.id, name: rec.name, tiles })
                }
                groupings.value = sections
            })
            .catch(() => {
                groupings.value = []
            })
    }

    return (
        <div
            className="searchIdle"
            visible={props.visible}
            onConnect={() => {
                fetchGroupingsOnce()
            }}
        >
            <div className="searchHistory" visible={hasHistory}>
                <h2>{getString('dmusic_find_search_history_text')}</h2>
                <div
                    className="searchHistoryList"
                    onConnect={(view) => {
                        // Re-render the list whenever the persisted
                        // history changes (record / clear). Mirrors
                        // GH's SearchHistory — a horizontal-scroll
                        // BauhausButtonNavigator with a leading
                        // icon-only "clear all" pill (cancel glyph)
                        // followed by one pill per stored query.
                        return history.onData((items) => {
                            view.innerHTML = ''
                            if (items.length > 0) {
                                view.append(
                                    <button
                                        className="searchHistoryItem searchHistoryClearItem"
                                        aria-label={getString('dmusic_clear_search')}
                                        onclick={() => clearSearchHistory()}
                                    >
                                        <Icon icon="cancel" />
                                    </button>
                                )
                            }
                            for (const q of items) {
                                view.append(
                                    <button
                                        className="searchHistoryItem"
                                        onclick={() => props.onSelect(q)}
                                    >
                                        {q}
                                    </button>
                                )
                            }
                        })
                    }}
                />
            </div>
            <div
                className="searchCategories searchCategoriesSkeleton"
                visible={groupings.map((v) => v === null)}
            >
                {...times(2, () => (
                    <div className="searchCategoryGrouping">
                        <div className="placeholder animated searchCategoriesSkeletonHeader" />
                        <div className="searchCategoriesGrid">
                            {...times(8, () => (
                                <div className="placeholder animated searchCategoryTileSkeleton" />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <div
                className="searchCategoryGroupings"
                visible={groupings.map((v) => v != null && v.length > 0)}
                onConnect={(view) =>
                    groupings.onData((sections) => {
                        view.innerHTML = ''
                        if (!sections) return
                        for (const section of sections) {
                            const grid = <div className="searchCategoriesGrid" />
                            for (const tile of section.tiles) {
                                grid.append(
                                    <button
                                        className="searchCategoryTile"
                                        onclick={() => {
                                            // GH's category tiles route to
                                            // the category's own pathname
                                            // (audiobook categories map
                                            // specially — see categoryHref).
                                            // Fall back to seeding the query
                                            // with the name when Firefly
                                            // didn't ship a routable url.
                                            if (tile.href) {
                                                navigateTo(tile.href)
                                            } else {
                                                props.onSelect(tile.name)
                                            }
                                        }}
                                    >
                                        <ImageView
                                            className="searchCategoryImage"
                                            image={tile.image}
                                            placeholder={<ImagePlaceholder />}
                                        />
                                        <span className="searchCategoryName">{tile.name}</span>
                                    </button>
                                )
                            }
                            view.append(
                                <section className="searchCategoryGrouping">
                                    <h2>{section.name}</h2>
                                    {grid}
                                </section>
                            )
                        }
                    })
                }
            />
        </div>
    )
}

const searchControllerRef = globalRef<MutableValue<SearchOptions | null>>(() =>
    data<SearchOptions | null>(null)
)

/**
 * A singleton to the search controller.
 *
 * The controller stores the live query string. Visibility of the search
 * page is now driven by the URL (`/search`), not by setting this to
 * null — see `getSearchMode()`.
 */
export function getSearchController() {
    return searchControllerRef.value
}

const searchRouteRef = globalRef<MutableValue<boolean>>(() => data(false))

/**
 * Tracks whether we're on the `/search` route. Wired to the router by
 * `Search` itself on mount; the page won't be reachable without that
 * mount happening, so this stays in sync.
 */
function getSearchRouteRef(): MutableValue<boolean> {
    return searchRouteRef.value
}

export function getSearchMode(): ObservableValue<boolean> {
    return getSearchRouteRef()
}

export function clearSearch() {
    // Drop the live query state and leave the search route if we're on
    // it. `back()` returns to wherever the user came from (e.g. home);
    // there's no harm in calling it from elsewhere — same-path
    // navigations are no-ops at the router layer.
    getSearchController().value = null
    if (window.location.pathname.startsWith('/search')) back()
}

export function showSearch(query?: string) {
    // Initialize the controller's query so the input pre-populates if a
    // caller seeded one (deep link with ?q=). The route mount kicks the
    // search-mode observable on, which the header listens to.
    getSearchController().value = { query: query ?? '' }
    const q = query?.trim()
    navigateTo(q ? `/search?q=${encodeURIComponent(q)}` : '/search')
}

/**
 * Push a query update while already on `/search` without spawning a
 * new history entry per keystroke — the URL stays canonical
 * (link-shareable, popstate-restorable) but the back button skips
 * over every partial query. Callers on the search route only.
 */
export function updateSearchQuery(query: string): void {
    const controller = getSearchController()
    const trimmed = query.trim()
    if (controller.value?.query !== query) {
        controller.value = { query }
    }
    const url = new URL(window.location.href)
    if (trimmed) {
        url.searchParams.set('q', trimmed)
    } else {
        url.searchParams.delete('q')
    }
    if (url.href !== window.location.href) {
        history.replaceState(history.state, '', url.toString())
    }
}
